import { ChangeDetectionStrategy, Component, inject, signal, DestroyRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { LoadingAnimationComponent } from '../../../shared/components/loading-animation/loading-animation.component';
import { TermsService, TermsContent } from '../../../shared/services/terms.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of } from 'rxjs';
import { containsDangerousPatterns } from '@shared/utils/html-security.utils';

@Component({
  selector: 'app-terms-editor',
  templateUrl: './terms-editor.component.html',
  styleUrls: ['./terms-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, LoadingAnimationComponent]
})
export class TermsEditorComponent {
  private readonly termsService = inject(TermsService);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management
  termsContent = signal('');
  originalTermsContent = signal('');
  isTermsEnabled = signal(true);
  isLoading = signal(false);
  isSaving = signal(false);
  isUpdatingStatus = signal(false);
  saveMessage = signal('');
  securityWarning = signal('');
  statusMessage = signal('');

  constructor() {
    this.loadTermsContent();
  }

  loadTermsContent(): void {
    this.isLoading.set(true);

    this.termsService.getTermsContentForAdmin().pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Error loading terms content:', error);
        this.saveMessage.set('Error loading terms content. Please try again.');
        return of({ termAndCondition: '<h2>Terms and Conditions</h2>\n<p>Please enter your terms and conditions content here...</p>', isTermsEnabled: true });
      }),
      finalize(() => this.isLoading.set(false))
    ).subscribe(response => {
      const content = response.termAndCondition || '';
      this.termsContent.set(content);
      this.originalTermsContent.set(content);
      this.isTermsEnabled.set(response.isTermsEnabled ?? true);
    });
  }

  onTermsContentChange(): void {
    this.saveMessage.set(''); // Clear any previous save messages
    this.securityWarning.set(''); // Clear any security warnings during editing
  }

  saveTermsContent(): void {
    if (this.isSaving()) return;

    this.isSaving.set(true);
    this.saveMessage.set('');

    const originalContent = this.termsContent();
    
    // Validate content is not empty
    if (!this.isValidTermsContent(originalContent)) {
      this.saveMessage.set('Terms and conditions content cannot be empty. Please enter some content.');
      this.isSaving.set(false);
      return;
    }

    // Check for dangerous content patterns
    if (containsDangerousPatterns(originalContent)) {
      this.showSecurityWarning();
    }

    // Send original content to backend - backend HtmlContentSanitizer handles security
    const request: TermsContent = { termAndCondition: originalContent };

    this.termsService.saveTermsContent(request).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Error saving terms content:', error);
        this.saveMessage.set('Error saving terms content. Please try again.');
        return of(null);
      }),
      finalize(() => this.isSaving.set(false))
    ).subscribe(response => {
      if (response) {
        this.saveMessage.set('Terms and conditions saved successfully!');

        // Reload content from backend to display the sanitized version
        this.loadTermsContent();

        // Clear success message after 3 seconds
        setTimeout(() => this.saveMessage.set(''), 3000);
      }
    });
  }

  discardChanges(): void {
    this.termsContent.set(this.originalTermsContent());
    this.saveMessage.set('Changes discarded');

    // Clear discard message after 2 seconds
    setTimeout(() => this.saveMessage.set(''), 2000);
  }

  hasUnsavedChanges(): boolean {
    return this.termsContent() !== this.originalTermsContent();
  }

  private showSecurityWarning(): void {
    this.securityWarning.set('Potentially harmful content will be sanitized by the server before saving.');
  
    // Clear warning after 5 seconds
    setTimeout(() => this.securityWarning.set(''), 5000);
  }

  private isValidTermsContent(content: string): boolean {
    return !(!content || content.trim() === '');
  }

  toggleTermsEnabled(): void {
    if (this.isUpdatingStatus()) return;

    const newStatus = !this.isTermsEnabled();
    this.isUpdatingStatus.set(true);
    this.statusMessage.set('');

    this.termsService.updateTermsEnabledStatus(newStatus).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Error updating terms status:', error);
        this.statusMessage.set('Error updating terms status. Please try again.');
        // Revert the toggle on error
        this.isTermsEnabled.set(!newStatus);
        return of(null);
      }),
      finalize(() => this.isUpdatingStatus.set(false))
    ).subscribe(response => {
      if (response) {
        this.isTermsEnabled.set(newStatus);
        this.statusMessage.set(`Terms and conditions ${newStatus ? 'enabled' : 'disabled'} successfully!`);
        
        // Clear success message after 3 seconds
        setTimeout(() => this.statusMessage.set(''), 3000);
      }
    });
  }
}
